import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch, type Control } from 'react-hook-form';

import { Alert, AlertDescription } from '@/components/ui/alert';
import type { ReceiveConfirmLineDto } from '@/entities/receive';

import { buildHandlingSummary } from '../lib/quantities';
import type { ConfirmArrivalFormValues } from '../model/types';

interface ConfirmArrivalHandlingSummaryProps {
  control: Control<ConfirmArrivalFormValues>;
  lines: ReceiveConfirmLineDto[];
}

/** The legacy dialog's "Handling quantity summary" banner, from a computed summary. */
export function ConfirmArrivalHandlingSummaryBanner({ summary }: { summary: { label: string; quantity: number }[] }) {
  const { t } = useTranslation();

  if (summary.length === 0) return null;

  return (
    <Alert>
      <AlertDescription>
        <span className="font-medium">{t('tracking.handling.summary.label', 'Handling quantity summary')}:</span>{' '}
        {summary.map((entry) => `${entry.quantity} ${entry.label}`).join(', ')}
      </AlertDescription>
    </Alert>
  );
}

/**
 * The receipt's summary banner.
 *
 * Its own component because it is the one thing here that needs *every* line's
 * quantities — subscribing to the whole `lines` array from the table would
 * re-render all rows on every keystroke.
 */
export function ConfirmArrivalHandlingSummary({ control, lines }: ConfirmArrivalHandlingSummaryProps) {
  const lineValues = useWatch({ control, name: 'lines' });
  const summary = useMemo(() => buildHandlingSummary(lines, lineValues ?? []), [lines, lineValues]);

  return <ConfirmArrivalHandlingSummaryBanner summary={summary} />;
}
